import axios from 'axios'
import { ErrorHandler } from './error-handler'
import type { ApiResponse } from '@/types'

// Create Axios instance with base configuration
// Using /api/proxy to route through Next.js API proxy and avoid CORS issues
const api = axios.create({
  baseURL: '/api/proxy',
  timeout: 60000,
})

// Request interceptor for adding auth token
// NOTE: Token refresh is disabled - backend does not support refresh tokens yet
// Token expires in 72 hours, user must login again after expiration
api.interceptors.request.use(
  async config => {
    // Get token from localStorage if available (client-side only)
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('auth-token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
      })
    }

    return config
  },
  error => {
    console.error('❌ Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for handling responses and errors
api.interceptors.response.use(
  response => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', {
        status: response.status,
        data: response.data,
      })
    }
    return response
  },
  error => {
    console.error('❌ Response Error:', error)
    console.log('Error status:', error)

    // Handle common error cases
    if (error.response?.status === 401) {
      console.log('🔒 401 Unauthorized - Logging out and redirecting to login')
      // Unauthorized - clear all auth data and redirect to login immediately
      if (typeof window !== 'undefined') {
        // Clear all auth-related localStorage items
        localStorage.removeItem('auth-token')
        localStorage.removeItem('auth-store')

        // Clear auth store state synchronously
        try {
          // Use synchronous import if possible, or handle async
          import('./store/auth-store')
            .then(({ useAuthStore }) => {
              useAuthStore.getState().clearAuth()
            })
            .catch(_err => {
              console.error('Failed to clear auth store:', _err)
            })
        } catch (_err) {
          console.error('Failed to import auth store:', _err)
        }

        // Get current path for redirect
        const currentPath = window.location.pathname

        // Perform immediate redirect using window.location for reliability
        // This ensures the redirect happens before any error handling can interfere
        if (
          currentPath !== '/magic-login' &&
          !currentPath.startsWith('/magic-login')
        ) {
          window.location.href = `/magic-login?redirect=${encodeURIComponent(currentPath)}`
        } else {
          window.location.href = '/magic-login'
        }

        // Note: We don't return Promise.reject here because the redirect will interrupt
        // any further processing. However, we still include it for completeness.
      }
    }

    return Promise.reject(error)
  }
)

// Generic API helper functions
export const apiHelpers = {
  // GET request
  get: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const response = await api.get(url)

      // For backend API endpoints that already return proper structure, return as-is
      if (url.includes('/api/advancing/') || url.includes('/api/artist/')) {
        return response.data
      }

      // Handle other API formats with statusCode
      if (
        response.data &&
        typeof response.data === 'object' &&
        'statusCode' in response.data
      ) {
        if (response.data.statusCode === 200) {
          return ErrorHandler.createSuccessResponse(response.data as T)
        } else {
          return ErrorHandler.createErrorResponse(response.data)
        }
      }

      return ErrorHandler.createSuccessResponse(response.data)
    } catch (error: unknown) {
      // Re-throw 401 errors to let the interceptor handle logout and redirect
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { status?: number } }
        if (axiosError.response?.status === 401) {
          throw error
        }
      }

      ErrorHandler.logError(error, `GET ${url}`)
      return ErrorHandler.createErrorResponse<T>(error)
    }
  },

  // POST request
  post: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await api.post(url, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      // For backend API endpoints that already return proper structure, return as-is
      if (url.includes('/api/advancing/') || url.includes('/api/artist/')) {
        return response.data
      }

      // Handle other API formats with statusCode
      if (
        response.data &&
        typeof response.data === 'object' &&
        'statusCode' in response.data
      ) {
        if (
          response.data.statusCode === 200 ||
          response.data.statusCode === 201
        ) {
          return ErrorHandler.createSuccessResponse(response.data as T)
        } else {
          return ErrorHandler.createErrorResponse(response.data)
        }
      }

      return ErrorHandler.createSuccessResponse(response.data)
    } catch (error: unknown) {
      // Re-throw 401 errors to let the interceptor handle logout and redirect
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response: { status?: number; data?: { statusCode?: number } }
        }
        if (axiosError.response?.status === 401) {
          throw error
        }
      }

      ErrorHandler.logError(error, `POST ${url}`)

      // Handle error responses with statusCode format for backend API
      if (
        url.includes('/api/advancing/') &&
        error &&
        typeof error === 'object' &&
        'response' in error
      ) {
        const axiosError = error as {
          response: { data: { statusCode?: number } }
        }
        if (axiosError.response?.data?.statusCode) {
          return axiosError.response.data as ApiResponse<T>
        }
      }

      return ErrorHandler.createErrorResponse<T>(error)
    }
  },

  // PUT request
  put: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await api.put(url, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      // For backend API endpoints that already return proper structure, return as-is
      if (url.includes('/api/advancing/') || url.includes('/api/artist/')) {
        return response.data
      }

      // Handle other API formats with statusCode
      if (
        response.data &&
        typeof response.data === 'object' &&
        'statusCode' in response.data
      ) {
        if (response.data.statusCode === 200) {
          return ErrorHandler.createSuccessResponse(response.data as T)
        } else {
          return ErrorHandler.createErrorResponse(response.data)
        }
      }

      return ErrorHandler.createSuccessResponse(response.data)
    } catch (error: unknown) {
      // Re-throw 401 errors to let the interceptor handle logout and redirect
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response: { status?: number; data?: { statusCode?: number } }
        }
        if (axiosError.response?.status === 401) {
          throw error
        }
      }

      ErrorHandler.logError(error, `PUT ${url}`)

      // Handle error responses with statusCode format for backend API
      if (
        url.includes('/api/advancing/') &&
        error &&
        typeof error === 'object' &&
        'response' in error
      ) {
        const axiosError = error as {
          response: { data: { statusCode?: number } }
        }
        if (axiosError.response?.data?.statusCode) {
          return axiosError.response.data as ApiResponse<T>
        }
      }

      return ErrorHandler.createErrorResponse<T>(error)
    }
  },

  // DELETE request
  delete: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const response = await api.delete(url)

      // For backend API endpoints that already return proper structure, return as-is
      if (url.includes('/api/advancing/') || url.includes('/api/artist/')) {
        return response.data
      }

      // Handle other API formats with statusCode
      if (
        response.data &&
        typeof response.data === 'object' &&
        'statusCode' in response.data
      ) {
        if (response.data.statusCode === 200) {
          return ErrorHandler.createSuccessResponse(response.data as T)
        } else {
          return ErrorHandler.createErrorResponse(response.data)
        }
      }

      return ErrorHandler.createSuccessResponse(response.data)
    } catch (error: unknown) {
      // Re-throw 401 errors to let the interceptor handle logout and redirect
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response: { status?: number; data?: { statusCode?: number } }
        }
        if (axiosError.response?.status === 401) {
          throw error
        }
      }

      ErrorHandler.logError(error, `DELETE ${url}`)

      // Handle error responses with statusCode format for backend API
      if (
        url.includes('/api/advancing/') &&
        error &&
        typeof error === 'object' &&
        'response' in error
      ) {
        const axiosError = error as {
          response: { data: { statusCode?: number } }
        }
        if (axiosError.response?.data?.statusCode) {
          return axiosError.response.data as ApiResponse<T>
        }
      }

      return ErrorHandler.createErrorResponse<T>(error)
    }
  },

  // POST request with multipart/form-data
  postMultipart: async <T>(
    url: string,
    formData: FormData
  ): Promise<ApiResponse<T>> => {
    try {
      // Don't set Content-Type manually - let axios/browser set it with boundary
      const response = await api.post(url, formData)

      // For backend API endpoints that already return proper structure, return as-is
      if (url.includes('/api/advancing/') || url.includes('/api/artist/')) {
        return response.data
      }

      // Handle other API formats with statusCode
      if (
        response.data &&
        typeof response.data === 'object' &&
        'statusCode' in response.data
      ) {
        if (
          response.data.statusCode === 200 ||
          response.data.statusCode === 201
        ) {
          return ErrorHandler.createSuccessResponse(response.data as T)
        } else {
          return ErrorHandler.createErrorResponse(response.data)
        }
      }

      return ErrorHandler.createSuccessResponse(response.data)
    } catch (error: unknown) {
      // Re-throw 401 errors to let the interceptor handle logout and redirect
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response: { status?: number; data?: { statusCode?: number } }
        }
        if (axiosError.response?.status === 401) {
          throw error
        }
      }

      ErrorHandler.logError(error, `POST MULTIPART ${url}`)

      // Handle error responses with statusCode format for backend API
      if (
        url.includes('/api/advancing/') &&
        error &&
        typeof error === 'object' &&
        'response' in error
      ) {
        const axiosError = error as {
          response: { data: { statusCode?: number } }
        }
        if (axiosError.response?.data?.statusCode) {
          return axiosError.response.data as ApiResponse<T>
        }
      }

      return ErrorHandler.createErrorResponse<T>(error)
    }
  },
}

export default api
